import React from 'react';
import { useTranslation } from 'react-i18next';

import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import {
  addDays,
  daysBetween,
  weekStart,
} from '@actual-app/core/shared/mtxbudget';
import type { Fortnight, PlanWeek } from '@actual-app/core/shared/mtxbudget';

import { useBankReads } from './bankReads';
import { useUnreadAccounts } from './recalcul';
import { hoursLabel, longDate, textDate } from './ui';

const weekdayDay = new Intl.DateTimeFormat('fr-CA', {
  weekday: 'long',
  day: 'numeric',
  timeZone: 'UTC',
});
const weekday = new Intl.DateTimeFormat('fr-CA', {
  weekday: 'long',
  timeZone: 'UTC',
});

const day = (date: string) => new Date(`${date}T00:00:00Z`);

/** « 1er », « 7 » : the day of the month as French writes it. */
function dayOfMonth(date: string): string {
  const n = day(date).getUTCDate();
  return n === 1 ? '1er' : String(n);
}

/** « du 1er au 7 novembre », « du 29 novembre au 5 décembre ». */
export function weekRange(start: string, end: string): string {
  return start.slice(0, 7) === end.slice(0, 7)
    ? `du ${dayOfMonth(start)} au ${textDate(end)}`
    : `du ${textDate(start)} au ${textDate(end)}`;
}

/** « Du lundi 26 au samedi 31 octobre » : the days of work of a week. */
export function workDays(start: string, end: string): string {
  return `Du ${weekdayDay.format(day(start))} au ${longDate(end)}`;
}

/** The week told from today: « La semaine prochaine », « Dans 2 semaines ». */
export function useWeekLabel(): (offset: number, today: string) => string {
  const { t } = useTranslation();
  return (offset, today) => {
    if (offset === 0) {
      // On Sunday the week of pay has begun, its work begins tomorrow.
      return day(today).getUTCDay() === 0
        ? t('La semaine qui commence demain')
        : t('Cette semaine');
    }
    if (offset === 1) {
      return t('La semaine prochaine');
    }
    if (offset === -1) {
      return t('La semaine dernière');
    }
    return offset > 1
      ? t('Dans {{count}} semaines', { count: offset })
      : t('Il y a {{count}} semaines', { count: -offset });
  };
}

/** « jeudi, dans 4 jours », « demain », or the date when it is further. */
export function useWhenPaid(): (payday: string, today: string) => string {
  const { t } = useTranslation();
  return (payday, today) => {
    const days = daysBetween(today, payday);
    if (days === 0) {
      return t("aujourd'hui");
    }
    if (days === 1) {
      return t('demain');
    }
    return days > 1 && days < 7
      ? t('{{weekday}}, dans {{days}} jours', {
          weekday: weekday.format(day(payday)),
          days,
        })
      : t('le {{date}}', { date: longDate(payday) });
  };
}

/** The hours of a week, with what the base leaves or lacks under them. */
export function WeekHours({
  week,
  big = false,
}: {
  week: PlanWeek;
  big?: boolean;
}) {
  const { t } = useTranslation();
  return (
    // At most half the row, its note going to the line: the week's name keeps
    // its room on a narrow phone.
    <View
      style={{ alignItems: 'flex-end', maxWidth: '50%', textAlign: 'right' }}
    >
      <Text
        style={{
          whiteSpace: 'nowrap',
          fontSize: big ? 22 : 18,
          fontWeight: big ? 700 : 600,
          color: theme.pageTextDark,
          ...styles.tnum,
        }}
      >
        {hoursLabel(week.hours)}
      </Text>
      <Text
        style={{
          fontSize: 12,
          color: week.aboveBase > 0 ? theme.warningText : theme.noticeTextLight,
        }}
      >
        {week.aboveBase > 0
          ? t('{{hours}} de plus que votre base', {
              hours: hoursLabel(week.aboveBase),
            })
          : t('votre base suffit')}
      </Text>
    </View>
  );
}

/** One week in a list: what it is, from today, and its hours. */
export function WeekRow({
  week,
  today,
  last,
  strong = false,
  withThreshold = false,
}: {
  week: PlanWeek;
  today: string;
  last?: boolean;
  strong?: boolean;
  withThreshold?: boolean;
}) {
  const { t } = useTranslation();
  const label = useWeekLabel();
  // On Sunday the work of this week begins tomorrow: its range starts Monday.
  const start =
    week.offset === 0 && day(today).getUTCDay() === 0
      ? addDays(week.start, 1)
      : week.start;
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        borderBottom: last ? undefined : `1px solid ${theme.tableBorder}`,
      }}
    >
      <View style={{ flex: 1 }}>
        <Text
          style={{
            fontWeight: strong ? 600 : 500,
            color: theme.pageTextDark,
          }}
        >
          {label(week.offset, today)}
        </Text>
        <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
          {withThreshold && week.aboveBase === 0
            ? t('{{range}} · {{hours}} suffiraient', {
                range: weekRange(start, week.end),
                hours: hoursLabel(week.threshold),
              })
            : weekRange(start, week.end)}
        </Text>
      </View>
      <WeekHours week={week} big={strong} />
    </View>
  );
}

function listFr(items: string[]): string {
  return items.length < 2
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}

/**
 * The banks that sent nothing since Sunday, and what the plan does about it:
 * a pay the banks have not shown yet is still counted.
 */
export function BanksBanner({
  plan,
  today,
}: {
  plan: Fortnight[];
  today: string;
}) {
  const { t } = useTranslation();
  const unread = useUnreadAccounts();
  const reads = useBankReads();
  if (unread.length === 0) {
    return null;
  }
  const banks = new Map<string, { dataDate: string; attention: boolean }>();
  for (const account of unread) {
    const read = account.accountId
      ? reads?.accounts[account.accountId]
      : undefined;
    const org = read?.org || account.name;
    const known = banks.get(org);
    const dataDate = read?.dataDate ?? '';
    banks.set(org, {
      dataDate:
        known && known.dataDate && known.dataDate < dataDate
          ? known.dataDate
          : dataDate,
      attention: Boolean(known?.attention) || Boolean(read?.needsAttention),
    });
  }
  const oldest = [...banks.values()]
    .map(bank => bank.dataDate)
    .filter(Boolean)
    .sort()[0];
  const reconnect = [...banks]
    .filter(([, bank]) => bank.attention)
    .map(([org]) => org);
  const silent = [...banks]
    .filter(([, bank]) => !bank.attention)
    .map(([org]) => org);
  const awaited = oldest
    ? plan.find(f => f.payday > oldest && f.payday <= addDays(today, 14))
    : undefined;
  const lastSunday = addDays(weekStart(today), -7);

  return (
    <View
      aria-live="polite"
      style={{
        gap: 4,
        padding: '12px 16px',
        borderRadius: 4,
        border: `1px solid ${theme.warningBorder}`,
        backgroundColor: theme.warningBackground,
      }}
    >
      <Text style={{ fontWeight: 600, color: theme.warningText }}>
        {!oldest
          ? t('Banques à relire')
          : oldest === lastSunday
            ? t('Banques lues dimanche dernier')
            : t('Banques lues le {{date}}', { date: textDate(oldest) })}
      </Text>
      <Text style={{ fontSize: 13, color: theme.warningTextLight }}>
        {[
          reconnect.length > 0 &&
            (reconnect.length === 1
              ? t('{{banks}} demande de vous reconnecter dans SimpleFIN.', {
                  banks: reconnect[0],
                })
              : t('{{banks}} demandent de vous reconnecter dans SimpleFIN.', {
                  banks: listFr(reconnect),
                })),
          silent.length > 0 &&
            t("{{banks}} n'a rien envoyé depuis dimanche.", {
              banks: listFr(silent),
            }),
          awaited &&
            t(
              "La paie du {{date}} est comptée tant que son dépôt n'apparaît pas.",
              { date: textDate(awaited.payday) },
            ),
        ]
          .filter(Boolean)
          .join(' ')}
      </Text>
    </View>
  );
}
